import React, { useEffect, useState } from "react";
import { Menu, Phone, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useT } from "@/lib/i18n";

export const Navbar = () => {
  const { t, lang, toggle, anchor } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-[background,box-shadow,border-color] duration-200 ${
        scrolled
          ? "bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b border-border shadow-soft"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <nav
        // Wider than the page container: eight links plus the action cluster
        // do not fit inside max-w-6xl once its padding is taken off.
        className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between gap-3 xl:gap-5"
        aria-label={t.site?.name ?? t.brand.name}
        data-testid="main-nav"
      >
        <a
          href={anchor("#top")}
          className="flex shrink-0 items-center gap-2 font-display text-base font-semibold text-foreground whitespace-nowrap"
          data-testid="nav-logo"
          aria-label={t.site?.name ?? t.brand.name}
        >
          <span
            aria-hidden
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            style={{ background: "hsl(var(--pine))", color: "hsl(var(--primary-foreground))" }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 20l9-14 9 14z" />
              <path d="M8 20v-5h8v5" />
            </svg>
          </span>
          <span>{t.site?.name ?? t.brand.name}</span>
        </a>

        {/* No min-w-0 here: the list must never shrink and clip a link. */}
        <ul className="hidden xl:flex items-center gap-0 2xl:gap-0.5">
          {t.nav.map((l) => (
            <li key={l.href}>
              <a
                href={anchor(l.href)}
                className="block px-2 xl:px-2.5 py-2 text-[13px] xl:text-sm text-foreground/80 hover:text-foreground rounded-md link-underline whitespace-nowrap"
                data-testid={`nav-link-${l.href.replace("#", "")}`}
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden xl:flex shrink-0 items-center gap-2">
          {/* The full number only fits on very wide screens; below that the
              icon keeps one-tap calling without crowding the nav links. */}
          <a
            href={t.brand.phoneHref}
            className="inline-flex shrink-0 items-center gap-1.5 h-10 rounded-lg border border-border bg-card px-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            title={t.brand.phone}
            aria-label={t.brand.phone}
            data-testid="nav-phone-link"
          >
            <Phone className="h-4 w-4 shrink-0" />
            <span className="hidden 2xl:inline whitespace-nowrap">{t.brand.phone}</span>
          </a>
          <button
            onClick={toggle}
            aria-label={t.ui.langToggleLabel}
            className="inline-flex shrink-0 items-center gap-1.5 h-10 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold uppercase tracking-wider text-foreground hover:bg-muted transition-colors"
            data-testid="nav-lang-toggle"
          >
            <Globe className="h-3.5 w-3.5 shrink-0" />
            <span aria-current={lang === "sk"} className={lang === "sk" ? "text-foreground" : "text-muted-foreground"}>SK</span>
            <span aria-hidden className="text-border">/</span>
            <span aria-current={lang === "en"} className={lang === "en" ? "text-foreground" : "text-muted-foreground"}>EN</span>
          </button>
          <Button asChild size="lg" className="h-11 rounded-xl whitespace-nowrap" data-testid="nav-reserve-button">
            <a href="#kontakt">{t.ui.reserve}</a>
          </Button>
        </div>

        <div className="xl:hidden flex items-center gap-2">
          <button
            onClick={toggle}
            aria-label={t.ui.langToggleLabel}
            className="inline-flex items-center h-9 rounded-lg border border-border bg-card px-2.5 text-[11px] font-semibold uppercase tracking-wider text-foreground"
            data-testid="nav-lang-toggle-mobile"
          >
            {lang === "sk" ? "SK" : "EN"}
          </button>
          <Button asChild size="sm" className="h-9 rounded-lg" data-testid="nav-reserve-button-mobile">
            <a href="#kontakt">{t.ui.reserve}</a>
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Menu"
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-card text-foreground"
                data-testid="nav-menu-trigger"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] sm:w-[380px]">
              <SheetTitle className="font-display text-xl">{t.brand.name}</SheetTitle>
              <ul className="mt-6 space-y-1">
                {t.nav.map((l) => (
                  <li key={l.href}>
                    <a
                      href={anchor(l.href)}
                      onClick={() => setOpen(false)}
                      className="block rounded-lg px-3 py-3 text-base text-foreground hover:bg-muted"
                      data-testid={`mobile-nav-link-${l.href.replace("#", "")}`}
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-6 space-y-2">
                <Button asChild size="lg" className="w-full rounded-xl h-12" data-testid="mobile-reserve-button">
                  <a href="#kontakt" onClick={() => setOpen(false)}>{t.hero.primaryCta}</a>
                </Button>
                <a
                  href={t.brand.phoneHref}
                  className="flex items-center justify-center gap-2 h-11 rounded-xl border border-border text-sm text-foreground"
                  data-testid="mobile-phone-link"
                >
                  <Phone className="h-4 w-4" />
                  {t.brand.phone}
                </a>
                <button
                  type="button"
                  onClick={() => { toggle(); setOpen(false); }}
                  aria-label={t.ui.langToggleLabel}
                  className="flex items-center justify-center gap-2 w-full h-11 rounded-xl border border-border text-sm text-foreground bg-card hover:bg-muted"
                  data-testid="mobile-sheet-lang-toggle"
                >
                  <Globe className="h-4 w-4" />
                  <span className={lang === "sk" ? "font-semibold text-foreground" : "text-muted-foreground"}>SK</span>
                  <span aria-hidden className="text-border">/</span>
                  <span className={lang === "en" ? "font-semibold text-foreground" : "text-muted-foreground"}>EN</span>
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
